import React from 'react';
import styled from 'astroturf/react';
import pluralize from 'pluralize';
import { SwitchTransition, Transition } from 'react-transition-group';

import {
  FADE_DURATION,
  CountText,
  TransitionState,
  transitions,
} from './fadeTransition';
import LoadingDotsBase from './LoadingDots';
import useScreenSize from 'js/utils/hooks/useScreenSize';
import { format, redirectToMarketing } from './util';
import { getSearchUpsellTracking } from './searchUpsellExperiment';
import { ImpressionWatcher } from 'js/utils/impressions/ImpressionWatcher';

type Props = {
  // For external styling
  className?: string;
  // Number of papers found by advanced search
  // If `null` is passed in, we show a loading indicator
  count: number | null;
  // The user's search query
  query: string;
  trackExperiment?: boolean;
};

const L_QUOTE = '\u201c';
const R_QUOTE = '\u201d';
// This is the same MOBILE_WIDTH as is in Search
// They should be the same number
const MOBILE_WIDTH = 750;

const Button = styled.button`
  background: #094ec3;
  border: 0;
  border-radius: 3px;
  color: #fff;
  font-size: 19px;
  font-weight: 500;
  letter-spacing: 0.86px;
  padding: 15px 20px;
  transition: 160ms background;

  &:hover {
    background: #2878ff;
  }

  @media (max-width: ${MOBILE_WIDTH}px) {
    width: 100%;
  }
`;

const Container = styled(ImpressionWatcher)`
  align-items: flex-start;
  background: #fff;
  background-image: url('/images/adv-search-bg-mirrored.png');
  background-position-x: 600px;
  // Chrome doesn't support this
  // so it falls back to the less good but serviceable
  // value above
  background-position-x: right -590px;
  background-position-y: 67%;
  background-size: 1000px;
  background-repeat: no-repeat;
  border: 1px solid #ddd;
  border-radius: 4px;
  box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.07);
  cursor: pointer;
  display: flex;
  flex-direction: column;
  justify-content: center;
  min-height: 238px;
  padding-left: 38px;

  @media (max-width: 903px) {
    background-position-x: 500px;
    // Chrome doesn't support this
    background-position-x: right -650px;
  }

  @media (max-width: 830px) {
    // Chrome doesn't support this
    background-position-x: right -750px;
  }

  @media (max-width: ${MOBILE_WIDTH}px) {
    background-image: none;
    border: 0;
    border-radius: 0;
    cursor: default;
    flex-direction: column;
    padding: 0 24px;
  }
`;

const LoadingDots = styled(LoadingDotsBase)`
  background: #0954d3;
`;

const PaperCount = styled.span`
  color: #094ec3;
`;

const Subtitle = styled.h2`
  color: #333;
  font-size: 24px;
  font-weight: 300;
  margin: 0 0 31px 0;

  @media (max-width: ${MOBILE_WIDTH}px) {
    font-size: 22px;
    margin-bottom: 19px;
  }
`;

const Title = styled.h1`
  color: #333;
  font-size: 32px;
  line-height: 1.4em;
  margin: 0 0 8px 0;
`;

const Upsell = ({
  className,
  count,
  query,
  trackExperiment = false,
}: Props) => {
  const { screenWidth: width } = useScreenSize();
  const redirect = () => redirectToMarketing(query, count, 'large-banner-tall');

  return (
    <Container
      {...getSearchUpsellTracking(
        'advanced_search',
        'search_inline',
        'large-banner-tall',
      )}
      active={trackExperiment}
      className={className}
      noBatch
      onClick={() => {
        // The container has no border on mobile,
        // so we don't want the whole thing to be clickable
        // to avoid misclicks and confusion
        if (width <= MOBILE_WIDTH) return;
        redirect();
      }}
    >
      <Title>
        Advanced Search found{' '}
        <PaperCount>
          <SwitchTransition>
            <Transition
              key={(count == null).toString()}
              timeout={FADE_DURATION}
            >
              {(state: TransitionState) =>
                count != null ? (
                  <CountText style={transitions[state]}>
                    {format(count)} {pluralize('Paper', count)}
                  </CountText>
                ) : (
                  <LoadingDots
                    marginRight={4}
                    // Have to put the transition in the style here
                    // since the way LoadingDots is written, using styled(LoadingDots)
                    // affects the individual dots, not the container
                    style={{
                      ...transitions[state],
                      transition: `${FADE_DURATION}ms opacity`,
                    }}
                  />
                )
              }
            </Transition>
          </SwitchTransition>
        </PaperCount>
      </Title>
      <Subtitle>
        with {L_QUOTE}
        {query}
        {R_QUOTE} in the full text
      </Subtitle>

      <Button onClick={redirect}>Upgrade to view results ▸</Button>
    </Container>
  );
};

export default Upsell;
